import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { displayAlertBoxSagaAction } from 'commons/src/actions';
import { toErrorTypeWithMessage } from 'commons/src/sagas/isErrorType';
import * as api from '../../../api/mqttPublisherApi';
import { MqttPublisherConfig, MqttPublisherFormValues } from './mqttPublisherTypes';

interface UseMqttPublisherConfigsReturn {
    configs: MqttPublisherConfig[];
    loading: boolean;
    error: string | undefined;
    createLoading: boolean;
    updateLoading: boolean;
    deleteLoading: boolean;
    refetch: () => Promise<void>;
    createConfig: (values: MqttPublisherFormValues) => Promise<MqttPublisherConfig>;
    updateConfig: (clientId: string, values: MqttPublisherFormValues) => Promise<MqttPublisherConfig>;
    deleteConfig: (clientId: string) => Promise<void>;
    updateActiveState: (clientId: string, active: boolean) => void;
}

// eslint-disable-next-line import/prefer-default-export
export const useMqttPublisherConfigs = (): UseMqttPublisherConfigsReturn => {
    const dispatch = useDispatch();
    const [configs, setConfigs] = useState<MqttPublisherConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | undefined>();
    const [createLoading, setCreateLoading] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const showError = useCallback(
        (err: unknown): void => {
            const description =
                err && typeof err === 'object' && 'description' in err
                    ? (err as { description: string }).description
                    : undefined;
            const { message } = toErrorTypeWithMessage(err);
            dispatch(displayAlertBoxSagaAction(description || message, true, true));
        },
        [dispatch]
    );

    const showSuccess = useCallback(
        (message: string): void => {
            dispatch(displayAlertBoxSagaAction(message, false, true));
        },
        [dispatch]
    );

    const refetch = useCallback(async () => {
        setLoading(true);
        setError(undefined);
        try {
            const result = await api.fetchMqttPublisherConfigs();
            setConfigs(result.configs);
        } catch (err) {
            const { message } = toErrorTypeWithMessage(err);
            setError(message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        let cancelled = false;

        const fetchInitial = async (): Promise<void> => {
            setLoading(true);
            setError(undefined);
            try {
                const result = await api.fetchMqttPublisherConfigs();
                if (!cancelled) setConfigs(result.configs);
            } catch (err) {
                if (!cancelled) {
                    const { message } = toErrorTypeWithMessage(err);
                    setError(message);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchInitial();
        return (): void => {
            cancelled = true;
        };
    }, []);

    const createConfig = async (values: MqttPublisherFormValues): Promise<MqttPublisherConfig> => {
        setCreateLoading(true);
        try {
            const created = await api.createMqttPublisherConfig(values);
            setConfigs(prev => [...prev, created]);
            return created;
        } catch (err) {
            showError(err);
            throw err;
        } finally {
            setCreateLoading(false);
        }
    };

    const updateConfig = async (clientId: string, values: MqttPublisherFormValues): Promise<MqttPublisherConfig> => {
        setUpdateLoading(true);
        try {
            const updated = await api.updateMqttPublisherConfig(clientId, values);
            setConfigs(prev => prev.map(c => (c.clientId === clientId ? updated : c)));
            showSuccess('MqttPublisher.SaveSuccess');
            return updated;
        } catch (err) {
            showError(err);
            throw err;
        } finally {
            setUpdateLoading(false);
        }
    };

    const deleteConfig = async (clientId: string): Promise<void> => {
        setDeleteLoading(true);
        try {
            await api.deleteMqttPublisherConfig(clientId);
            setConfigs(prev => prev.filter(c => c.clientId !== clientId));
            showSuccess('MqttPublisher.DeleteSuccess');
        } catch (err) {
            showError(err);
            throw err;
        } finally {
            setDeleteLoading(false);
        }
    };

    const updateActiveState = (clientId: string, active: boolean): void => {
        const config = configs.find(c => c.clientId === clientId);
        if (!config) return;

        setConfigs(prev => prev.map(c => (c.clientId === clientId ? { ...c, active } : c)));
        api.updateMqttPublisherConfig(clientId, {
            name: config.name,
            protocol: config.protocol,
            host: config.host,
            port: config.port,
            authType: config.authType,
            username: config.username ?? undefined,
            subscribedToAll: config.subscribedToAll,
            eventTypes: config.eventTypes,
            locations: config.locations,
            sensorUnits: config.sensorUnits,
            active,
        })
            .then(updated => {
                setConfigs(prev => prev.map(c => (c.clientId === clientId ? updated : c)));
            })
            .catch(err => {
                setConfigs(prev => prev.map(c => (c.clientId === clientId ? { ...c, active: !active } : c)));
                showError(err);
            });
    };

    return {
        configs,
        loading,
        error,
        createLoading,
        updateLoading,
        deleteLoading,
        refetch,
        createConfig,
        updateConfig,
        deleteConfig,
        updateActiveState,
    };
};
